<template>
	<div class="ProgrammePrice">
		<span
			v-if="hasTuition && originalOffer"
			class="OriginalOffer"
		>
			{{ originalOffer }}/{{ durationPeriod }}
		</span>
		<div class="Price">
			<span
				v-skeleton="{
					loading,
					sizes: {
						Small: { width: '2.938rem', height: '1rem' },
					},
					margins: {
						Small: '0 0.5rem 0 0',
					}
				}"
				class="Duration"
			>
				{{ durationLong }}
			</span>
			<span
				v-skeleton="{
					loading,
					sizes: {
						Small: { width: '6.688rem', height: '1rem' },
					}
				}"
				class="CurrentPrice"
			>
        <template v-if="hasTuition && tuition.amount > 0">
			<b>{{ price }}</b>/{{ durationPeriod }}
        </template>
		<template v-else-if="hasTuition">
			<b>Free</b>
		</template>
        <template v-else>
			<b>Tuition not available</b>
        </template>
			</span>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useSearchStore } from '@stores/useSearchStore';
import type ITuition from '@app/interfaces/dtos/ITuition';
import type { PropType } from 'vue';

const props = defineProps({
	tuition: {
		type: Object as PropType<ITuition | null>,
		default: null,
	},
	duration: {
		type: String,
		default: '',
	},
	loading: {
		type: Boolean,
		default: false,
	},
});

const hasTuition = computed(() => !!props.tuition);

const searchStore = useSearchStore();
const { userNationality } = storeToRefs(searchStore);
const userLocale = computed(() => userNationality.value || 'en-US');

const formatCurrency = (amount: number, currency: string,) => {
	try {
		return new Intl.NumberFormat(userLocale.value, {
			style: 'currency',
			currency: currency,
			maximumFractionDigits: 0
		}).format(amount);
	} catch (err) {
		console.warn('Failed to format tuition', err);
		return `${amount} ${currency}`;
	}
};


const price = computed(() => {
	if(props.tuition === null) return '';

	const amount = props.tuition.discountedAmount || props.tuition.amount;
	return formatCurrency(amount, props.tuition.currency);
});

const originalOffer = computed(() =>

	props.tuition?.discountedAmount ? formatCurrency(props.tuition.amount, props.tuition.currency) : ''
);
const durationLong = computed(() => props.duration || '');
const durationPeriod = computed(() => {
	const periodLower = props.tuition?.period?.toLowerCase();


	if (periodLower?.includes('year')) return 'yr';
	if (periodLower?.includes('month')) return 'period';
	if (periodLower?.includes('week')) return 'wk';
	if (periodLower?.includes('day')) return 'day';

	return props.tuition?.period;
});
</script>

<style scoped lang="scss">
	.ProgrammePrice {
		display: flex;
		flex-direction: column;
		align-self: flex-end;
		width: 100%;

		@include MarginAreas(0.5, 0, 0, 0);

		.OriginalOffer {
			align-self: flex-end;
			text-decoration: line-through;
			color: $Grey-900;

			@include TextStyle(UiSmallRegular);
		}

		.Price {
			align-self: flex-end;
			.Duration {
				color: $Grey-900;

				@include TextStyle(UiSmallRegular);
			}

			.CurrentPrice {
				color: $Grey-900;

				@include TextStyle(UiSmallRegular);

				b {
					@include TextStyle(UiMediumBold);
				}

				&::before {
					content: '•';
					@include MarginAreas(0, 0.25);
				}
			}
		}
	}
</style>
